/**
 * Снаряд: what a танк fires.
 *
 * Flat and fast where a вогняний снаряд from a требушет is slow and high -
 * the two are different weapons and the difference is the arc. A shell goes
 * where the gun is pointing at forty blocks a second and drops about a block
 * over the first fifty, so aiming at something far away is a thing you learn
 * rather than a thing you calculate.
 *
 * What it hits, it hits *once*: the first tank or the first block, and then
 * it is gone. That is the whole of the rule that makes five hits mean five -
 * a shell that could clip two tanks would make the number an estimate.
 *
 * Pure: no world, no renderer. `stepShells` is given a way to ask whether a
 * cell is solid and a list of tanks, and hands back what was struck.
 */

import { SHELL_GRAVITY, shellHits, type Tank } from "./tank.js";

export interface Shell {
  readonly id: number;
  /** Whose tank fired it, so a tank cannot shell itself. */
  readonly from: number;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  /** Seconds it has been in the air. They do not fly for ever. */
  age: number;
}

/** How long one stays in the air before it is given up on. */
export const SHELL_LIFE = 6;

let nextId = 1;

export function makeShell(
  at: { x: number; y: number; z: number },
  velocity: { x: number; y: number; z: number },
  from: number,
): Shell {
  return {
    id: nextId++, from,
    x: at.x, y: at.y, z: at.z,
    vx: velocity.x, vy: velocity.y, vz: velocity.z,
    age: 0,
  };
}

/** What a shell found at the end of its flight. */
export interface Impact {
  readonly shell: Shell;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  /** The tank it struck, or null for the ground. */
  readonly tank: Tank | null;
}

/**
 * Fly them for `dt` seconds.
 *
 * Marched in small steps rather than in one jump, because at forty blocks a
 * second a single step of a fifteenth of a second is nearly three blocks - a
 * shell would fly through a tank, through a wall, and out the other side of
 * both. The step is a third of a block, which is smaller than anything it
 * could be asked to hit.
 *
 * Struck shells are taken out of the list here; the caller gets the impacts.
 */
export function stepShells(
  shells: Shell[],
  dt: number,
  isSolid: (x: number, y: number, z: number) => boolean,
  tanks: readonly Tank[],
): readonly Impact[] {
  const impacts: Impact[] = [];

  for (let i = shells.length - 1; i >= 0; i--) {
    const shell = shells[i]!;
    shell.age += dt;
    if (shell.age > SHELL_LIFE) { shells.splice(i, 1); continue; }

    const speed = Math.hypot(shell.vx, shell.vy, shell.vz);
    const steps = Math.max(1, Math.ceil((speed * dt) / 0.33));
    const step = dt / steps;
    let done = false;

    for (let s = 0; s < steps && !done; s++) {
      shell.vy -= SHELL_GRAVITY * step;
      shell.x += shell.vx * step;
      shell.y += shell.vy * step;
      shell.z += shell.vz * step;

      for (const tank of tanks) {
        if (tank.id === shell.from) continue;
        if (!shellHits(tank, shell)) continue;
        impacts.push({ shell, x: shell.x, y: shell.y, z: shell.z, tank });
        done = true;
        break;
      }
      if (done) break;

      if (isSolid(Math.floor(shell.x), Math.floor(shell.y), Math.floor(shell.z))) {
        impacts.push({
          shell, x: shell.x, y: shell.y, z: shell.z, tank: null,
        });
        done = true;
      }
    }
    if (done) shells.splice(i, 1);
  }

  return impacts;
}
